/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { CosmosTabs } from "@components/elements/molecules/cosmos-tabs";
import { useCallback } from "react";
import type { FilterOption } from "../core/Directory.types";

export type DirectoryTabsProps = {
	options: FilterOption[];
	value: string;
	onChange: (value: string) => void;
	/** Accessible name for the tablist (WCAG 4.1.2). Required. */
	label: string;
};

/**
 * Single-value tab filter (used for both `tabs` and `tabsMulti` filter kinds —
 * the engine does the dispatch; this element only renders the UI).
 *
 * Clicking the active tab clears the selection (`canToggleTabs` via CosmosTabs).
 */
export function DirectoryTabs({ options, value, onChange, label }: DirectoryTabsProps): React.JSX.Element {
	if (process.env.NODE_ENV !== "production" && !label.trim()) {
		console.warn(
			"[DirectoryTabs] An empty `label` was passed; the rendered tablist will have no accessible name. Supply a meaningful label at the FilterDef.",
		);
	}

	const activeIndex = value ? options.findIndex((o) => o.value === value) : -1;

	const handleChange = useCallback(
		(index: number | undefined) => {
			if (index === undefined || index < 0) {
				onChange("");
				return;
			}
			const option = options[index];
			if (option && !option.disabled) onChange(option.value);
		},
		[onChange, options],
	);

	return (
		<CosmosTabs
			activeIndex={activeIndex >= 0 ? activeIndex : undefined}
			onActiveIndexChange={handleChange}
			canToggleTabs
			inactiveVariant="outline"
		>
			<CosmosTabs.ButtonList aria-label={label}>
				{options.map((option, index) => (
					<CosmosTabs.Button key={option.value} index={index} label={option.label} disabled={option.disabled} />
				))}
			</CosmosTabs.ButtonList>
		</CosmosTabs>
	);
}
